import authAxios from 'src/utils/authAxios';
import { riwLog } from 'src/hooks/riw-logs';

export interface RewardsBalance {
  balance: number;
  earnedTotal: number;
  redeemedTotal: number;
}

export interface RewardSummaryCategory {
  category: string;
  type: 'earned' | 'redeemed';
  count: number;
  totalPoints: number;
  lastAwardedAt?: Date;
  lastSeenAt?: Date;
  pendingNotification?: {
    message: string;
    createdAt: Date;
  };
  pendingCount?: number;
  pendingPoints?: number;
  metadata?: {
    taskIds?: string[];
    redemptionType?: string;
    referredCompanyIds?: string[];
    [key: string]: any;
  };
}

export interface PerkStatus {
  unlocked: boolean;
  claimed: boolean;
  minPaidMonths?: number;
}

export interface RewardsSummary {
  balance: number;
  totalReferralSignups?: number;
  /** How many points convert to one contest entry. Points are hidden from members. */
  pointsPerEntry?: number;
  /** Successful subscription payments so far (≈ paid months on monthly plans). */
  paidMonths?: number;
  /** Auto-unlock perk state, keyed by perk: member_offers, wallet, water_bottle. */
  perks?: {
    [key: string]: PerkStatus;
  };
  categories: {
    [key: string]: RewardSummaryCategory;
  };
}

export interface RedemptionRequest {
  redemptionType: 'wallet' | 'water_bottle' | 'member_offers_unlock';
  quantity?: number;
  deliveryInfo?: {
    address: string;
    contactEmail?: string; // Optional, will use account email if not provided
  };
}

export interface RewardItemDetails {
  id: 'wallet' | 'water_bottle' | 'member_offers_unlock';
  name: string;
  description: string;
  imageUrl: string;
  specsMarkdown?: string;
  specs: Array<{ label: string; value: string }>;
  points: number;
}

/**
 * Get all reward items from Notion-backed catalog
 */
export const getRewardCatalog = async (
  companyId: string
): Promise<RewardItemDetails[]> => {
  try {
    const axiosReq = await authAxios();
    const response = await axiosReq.get<RewardItemDetails[]>(
      `/rewards/catalog/${companyId}`
    );
    return response.data;
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Company ID: ${companyId}`,
      'Failed to get rewards catalog',
      error
    );
    return [];
  }
};

/**
 * Get rewards balance for the current company
 */
export const getRewardsBalance = async (
  companyId: string
): Promise<RewardsBalance> => {
  try {
    const axiosReq = await authAxios();
    const response = await axiosReq.get<RewardsBalance>(
      `/rewards/balance/${companyId}`
    );
    return response.data;
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Company ID: ${companyId}`,
      'Failed to get rewards balance',
      error
    );
    throw error;
  }
};

/**
 * Get detailed rewards summary with category breakdown
 */
export const getRewardsSummary = async (
  companyId: string
): Promise<RewardsSummary> => {
  try {
    const axiosReq = await authAxios();
    const response = await axiosReq.get<RewardsSummary>(
      `/rewards/summary/${companyId}`
    );
    return response.data;
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Company ID: ${companyId}`,
      'Failed to get rewards summary',
      error
    );
    throw error;
  }
};

/**
 * Redeem points for an item
 */
export const redeemPoints = async (
  companyId: string,
  redemptionRequest: RedemptionRequest
): Promise<{ success: boolean; message: string }> => {
  try {
    const axiosReq = await authAxios();
    const response = await axiosReq.post(
      `/rewards/redeem/${companyId}`,
      redemptionRequest
    );
    return response.data;
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Company ID: ${companyId}`,
      'Failed to redeem points',
      error
    );
    throw error;
  }
};

/**
 * Get pending reward notifications
 */
export const getPendingNotifications = async (
  companyId: string
): Promise<
  Array<{
    category: string;
    message: string;
    createdAt: Date;
    points: number;
    count: number;
  }>
> => {
  try {
    const axiosReq = await authAxios();
    const response = await axiosReq.get(`/rewards/notifications/${companyId}`);
    return response.data;
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Company ID: ${companyId}`,
      'Failed to get pending notifications',
      error
    );
    throw error;
  }
};

/**
 * Dismiss notifications for a category
 */
export const dismissNotifications = async (
  companyId: string,
  category: string
): Promise<void> => {
  try {
    const axiosReq = await authAxios();
    await axiosReq.post(
      `/rewards/notifications/${category}/dismiss/${companyId}`
    );
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Company ID: ${companyId}`,
      'Failed to dismiss notifications',
      error
    );
    throw error;
  }
};

/**
 * Get reward item details from Notion
 */
export const getRewardItemDetails = async (
  companyId: string,
  itemType: 'wallet' | 'water_bottle'
): Promise<RewardItemDetails> => {
  try {
    const axiosReq = await authAxios();
    const response = await axiosReq.get<RewardItemDetails>(
      `/rewards/item-details/${itemType}/${companyId}`
    );
    return response.data;
  } catch (error) {
    riwLog(
      'error',
      'Rewards.ts',
      `Item Type: ${itemType}`,
      'Failed to get reward item details',
      error
    );
    throw error;
  }
};
